import React, { useMemo, useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import { byRecentAssignment, fmtLastWorked } from '../utils/people';

/**
 * Open shift → "Give it to…" (owner, Oct 22 2026: "on open shifts allow me
 * to already select someone as claimed"). The office picks who has the shift;
 * the server records a claim by that person made from the office and does
 * what their own tap would — every job of the shift is theirs and the offer
 * closes (POST /jobs/:id/open-shift/claim-for). Two ticks, both on: text them
 * it's theirs, and tell everyone else who was texted that it's taken. A filled
 * shift shows who has it and offers "Give it to someone else instead" — the
 * person it comes from is texted too.
 *
 * The same people a shift can be offered to (PeoplePicker's rule): a login
 * wearing the Employee hat, never an AI profile — recent people first.
 */

const isAiName = (name: any) => /\bai\b/i.test(String(name || ''));
const canWorkShift = (p: any) => (p?.hats || []).includes('crew') && !isAiName(p?.name);
const firstName = (name: any) => String(name || '').trim().split(/\s+/)[0] || 'them';

export interface GiveShiftToProps {
  jobId: string | number;
  /** Who has it now (the panel's claims), first claimer first. */
  claims: { id: number; name: string }[];
  required: number;
  filled: boolean;
  /** "all 3 jobs on Sat, Oct 10" for a day offered as one shift; null for one job's shift. */
  dayWords?: string | null;
  disabled?: boolean;
  /** The server's answer, for the panel to refresh from and to show any texts left to send. */
  onDone: (r: any) => void;
  className?: string;
}

export default function GiveShiftTo({ jobId, claims, required, filled, dayWords = null, disabled = false, onDone, className = '' }: GiveShiftToProps) {
  const [open, setOpen] = useState(false);
  const [who, setWho] = useState<number | ''>('');
  const [textThem, setTextThem] = useState(true);
  const [tellOthers, setTellOthers] = useState(true);
  const [insteadOf, setInsteadOf] = useState<number | ''>('');

  // Same query and cache as PeoplePicker and Create Job's "Assign employees" chips.
  const { data: employeesData = [] } = useQuery('employees', () =>
    api.get('/employees').then(r => r.data.employees as any[]), { enabled: open });
  const taken = useMemo(() => new Set(claims.map(c => c.id)), [claims]);
  const people = useMemo(() => (employeesData as any[])
    .filter((e: any) => canWorkShift(e) && !taken.has(e.id))
    .sort(byRecentAssignment), [employeesData, taken]);

  const what = dayWords || 'this shift';
  // Taking it from someone: one spot → the one who has it; more → he picks which.
  const replacing = filled && claims.length > 0;
  const fromId = replacing ? (claims.length === 1 ? claims[0].id : insteadOf) : '';
  const from = replacing ? claims.find(c => c.id === fromId) || null : null;

  const give = useMutation(() => api.post(`/jobs/${jobId}/open-shift/claim-for`, {
    crew_member_id: who, text_them: textThem, tell_others: tellOthers,
    ...(replacing && fromId ? { instead_of: fromId } : {}),
  }).then(r => r.data), {
    onSuccess: (r: any) => {
      if (r?.pending) return;
      setOpen(false); setWho(''); setInsteadOf('');
      toast.success(r?.message || 'Done');
      onDone(r);
    },
    onError: (e: any) => {
      const d = e?.response?.data;
      toast.error(d?.message || 'Could not give them the shift');
      // Someone claimed it while the form was open: the panel reads itself again and shows who.
      if (e?.response?.status === 409) onDone(null);
    },
  });

  const chosen = people.find((p: any) => p.id === who);
  const canGive = !!who && !give.isLoading && !disabled && (!replacing || !!fromId);

  if (!open) {
    return (
      <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${className}`}>
        {filled && claims.length > 0 && (
          <span className="text-orange-900">
            <span className="font-semibold">{claims.map(c => c.name).join(' and ')}</span> {claims.length === 1 ? 'has' : 'have'} it.
          </span>
        )}
        <button type="button" onClick={() => setOpen(true)} disabled={disabled}
          className="font-medium text-orange-800 hover:text-orange-950 underline disabled:opacity-50">
          {filled && claims.length > 0 ? 'Give it to someone else instead' : 'Give it to…'}
        </button>
        {!filled && <span className="text-[11px] text-gray-500">Already know who's taking it? Put them on it from here.</span>}
      </div>
    );
  }

  return (
    <div className={`rounded-lg border border-orange-200 bg-white px-3 py-2.5 space-y-2 ${className}`}>
      <p className="text-xs font-semibold text-orange-900">Give {what} to</p>
      <select
        aria-label="Who gets the shift"
        className="input text-sm w-full"
        value={who}
        onChange={e => setWho(e.target.value ? Number(e.target.value) : '')}
      >
        <option value="">Pick a person…</option>
        {people.map((p: any) => (
          <option key={p.id} value={p.id}>{p.name} · last worked {fmtLastWorked(p.last_assigned_date)}</option>
        ))}
      </select>
      {people.length === 0 && (
        <p className="text-xs text-amber-700">Nobody to pick — only people with the Employee hat on their profile can take a shift.</p>
      )}
      {replacing && claims.length > 1 && (
        <label className="block text-xs text-gray-700">
          Instead of
          <select className="input text-sm w-full mt-1" value={insteadOf} onChange={e => setInsteadOf(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Pick who comes off…</option>
            {claims.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
      )}
      {from && (
        <p className="text-xs text-amber-700">{from.name} comes off {what}{textThem || tellOthers ? ' and gets a text saying so' : ''}.</p>
      )}
      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
        <input type="checkbox" checked={textThem} onChange={e => setTextThem(e.target.checked)} className="rounded border-gray-300 text-brand-600" />
        Text them it's theirs
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
        <input type="checkbox" checked={tellOthers} onChange={e => setTellOthers(e.target.checked)} className="rounded border-gray-300 text-brand-600" />
        Tell the others it's taken
        <span className="text-[11px] text-gray-500">(everyone who got the offer text)</span>
      </label>
      {required > 1 && claims.length + 1 < required && !replacing && (
        <p className="text-[11px] text-gray-500">{required} people needed — it stays offered for the other {required - claims.length - 1}, so the others aren't told yet.</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => give.mutate()} disabled={!canGive} className="btn-primary text-xs py-1.5 px-3 disabled:opacity-40">
          {give.isLoading ? 'Giving…' : chosen ? `Give it to ${firstName(chosen.name)}` : 'Give it'}
        </button>
        <button type="button" onClick={() => { setOpen(false); setWho(''); setInsteadOf(''); }} className="btn-secondary text-xs py-1.5 px-3">Cancel</button>
      </div>
    </div>
  );
}
